<script>
import { CurrencyFormatter } from '../../../common/currency';
import { dateFormatter } from '../../../common/utils';
import { VISA, MASTERCARD, TELERED, CLAVE } from '../../../common/card-utils';

    export default {
        name: 'Voucher',
        emits: ['update:modelValue'],
        inject: ['t'],
        props: {
            transaction: {
                type: Object,
                required: true,
                default: null
            },
            modelValue: {
                type: Boolean,
                required: true,
            },
            enableBtn: {
                type: Boolean,
                default: true
            }
        },
        computed: {
            getDate(){
                return dateFormatter(this.transaction.processed_at ?? this.transaction.created_at);
            },
            getStatus(){
                return this.t ? this.t('transactions.status.' + this.transaction.status) : this.transaction.status;
            },
            getTotal(){
                let tip = 0;

                if('tip' in this.transaction.metadatas){
                    tip = this.transaction.metadatas.tip;
                }

                let total = this.transaction.amount + (this.transaction.tax ?? 0) + Number.parseInt(tip);
                return '$' + CurrencyFormatter.format(total/100);
            },
            getCardBrandLogo(){
                let cardBrand = this.transaction.metadatas?.card_brand;
                let img = null;

                switch (cardBrand) {
                    case VISA:
                        img = '/img/card_brands/visa.svg';
                        break;
                    case MASTERCARD:
                        img = '/img/card_brands/mastercard.svg';
                        break;
                    case TELERED:
                        img = '/img/card_brands/clave.svg';
                        break;
                    case CLAVE:
                        img = '/img/card_brands/clave.svg';
                        break;
                    default:
                        break;
                }

                return img;
            },
            lastPanDigits(){
                return this.transaction.pan.slice(-4);
            },
            getProducts(){
                if('checkout_items' in this.transaction.metadatas){
                    let products = JSON.parse(this.transaction.metadatas.checkout_items);

                    return products.map(p => {
                        let price = p.price;
                        //Fix NaN in voucher when tax not exists
                        let taxPercentage = p.tax || 0;
                        let tax = price*taxPercentage;
                        let subtotal = p.quantity * (price + tax);
                        return {
                            id: p.id,
                            name: p.name,
                            price: (price/100).toFixed(2),
                            taxPercentage: (taxPercentage*100).toFixed(2),
                            tax: (tax/100).toFixed(2),
                            quantity: p.quantity,
                            subtotal: (subtotal/100).toFixed(2)
                        };
                    });
                }

                return [];
            },
            hasProducts(){
                return this.getProducts.length > 0;
            },
        }
    }

</script>

<template>
    <slot
        v-bind:getDate="getDate"
        v-bind:getStatus="getStatus"
        v-bind:lastPanDigits="lastPanDigits"
        v-bind:getCardBrandLogo="getCardBrandLogo"
        v-bind:getTotal="getTotal"
    >
        <div class="card shadow mt-4">
            <div class="card-body">
                <table class="table table-bordered">
                    <thead>
                        <tr>
                            <th>Recibo</th>
                            <th>#{{ transaction?.identifier }}</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr>
                            <td>Fecha</td>
                            <td>{{ getDate }}</td>
                        </tr>

                        <tr>
                            <td>Nº Autorización</td>
                            <td>{{ transaction?.authorization_number }}</td>
                        </tr>

                        <tr>
                            <td>Estado</td>
                            <td>
                                <template v-if="transaction.status === 'authorized'">
                                    <i class="small fa fa-check-circle text-success"></i>
                                </template>
                                <template v-else>
                                    <i class="small fa fa-circle-xmark text-danger"></i>
                                </template>

                                {{ this.getStatus }}

                            </td>
                        </tr>

                        <tr>
                            <td>Titular</td>
                            <td>{{ transaction.card_holder }}</td>
                        </tr>

                        <tr>
                            <td>Tarjeta</td>
                            <td>
                                <span style="display: inline-flex; align-items: center; gap: 0.5rem;">
                                    <img v-if="getCardBrandLogo" :src="getCardBrandLogo" alt="Card brand" width="30">
                                    {{ transaction.pan.slice(-4) }}
                                </span>
                            </td>
                        </tr>

                        <tr v-if="!hasProducts">
                            <td>Total</td>
                            <td>{{ getTotal }}</td>
                        </tr>
                    </tbody>

                </table>

                <div v-if="hasProducts">
                    <table class="table table-bordered table-sm">
                        <thead class="table-light">
                        <tr>
                            <th scope="col" colspan="6">Detalle de Productos</th>
                        </tr>
                        <tr>
                            <th scope="col">Nombre</th>
                            <th scope="col">Precio Unitario</th>
                            <th scope="col">Impuestos</th>
                            <th scope="col">ITBMS</th>
                            <th scope="col">Cantidad</th>
                            <th scope="col">Subtotal</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="product in getProducts" :key="product.id">
                            <td>{{ product.name }}</td>
                            <td>${{ product.price }}</td>
                            <td>${{ product.tax }}</td>
                            <td>{{ product.taxPercentage }}%</td>
                            <td>{{ product.quantity }}</td>
                            <td>${{ product.subtotal }}</td>
                        </tr>
                        </tbody>
                        <tfoot>
                        <tr>
                            <td colspan="5" class="table-light"><strong>Total:</strong></td>
                            <td class="table-light"><strong>{{ getTotal }}</strong></td>
                        </tr>
                        </tfoot>
                    </table>
                </div>

                <button
                    v-if="enableBtn"
                    type="button"
                    class="btn btn-primary btn-block"
                    @click="$emit('update:modelValue', !modelValue)"
                >
                    Aceptar
                </button>

            </div>

        </div>
    </slot>
</template>
